// @ai-generated 2026-08-04T00:00:00+00:00 by malte.ulfers@tngtech.com using Claude Opus 4.7
// Flat registry of every field path referenceable from a ruleset AST, across all validation
// shapes (order, order_position, address, service). One lookup for the whole app — the visitor
// hits the same path either at the top of a shape or after descending into a for_each body, and
// it just needs to find the label + type.
import { SynqoAggregateFunction, SynqoCompareGranularity, SynqoCompareOperator, SynqoUnaryOperator } from '@Model';

export type RulesetFieldType = 'decimal' | 'enum' | 'string' | 'bool' | 'date';

export interface RulesetFieldMetadata {
    path: string;
    label: string;
    type: RulesetFieldType;
    /** For enums: valid values as they appear in the AST literal (matches CLR names). */
    enumValues?: readonly string[];
}

// Keyed by the numeric enum member, because that is what the API sends — see the wire-format note
// in Model/Rulesets/types.ts. Using the enum members as keys keeps the mapping checked by tsc: a
// renamed or reordered member breaks the build instead of silently rendering a raw ordinal.
export const COMPARE_OPERATOR_LABEL: Record<SynqoCompareOperator, string> = {
    [SynqoCompareOperator.Eq]: 'ist gleich',
    [SynqoCompareOperator.Neq]: 'ist ungleich',
    [SynqoCompareOperator.Gt]: 'ist größer als',
    [SynqoCompareOperator.Gte]: 'ist größer oder gleich',
    [SynqoCompareOperator.Lt]: 'ist kleiner als',
    [SynqoCompareOperator.Lte]: 'ist kleiner oder gleich',
};

// Phrased as full predicates because they render standalone, without a right-hand side.
export const UNARY_OPERATOR_LABEL: Record<SynqoUnaryOperator, string> = {
    [SynqoUnaryOperator.IsNull]: 'ist nicht gesetzt',
    [SynqoUnaryOperator.IsNotNull]: 'ist gesetzt',
    [SynqoUnaryOperator.IsEmpty]: 'ist leer',
    [SynqoUnaryOperator.IsNotEmpty]: 'ist befüllt',
};

// Only rendered for the non-default granularity: an Exact comparison is what a reader assumes, while
// a date-level one silently ignores the time of day and has to say so.
export const COMPARE_GRANULARITY_LABEL: Record<SynqoCompareGranularity, string | undefined> = {
    [SynqoCompareGranularity.Exact]: undefined,
    [SynqoCompareGranularity.Date]: 'tagesgenau',
};

export const AGGREGATE_FUNCTION_LABEL: Record<SynqoAggregateFunction, string> = {
    [SynqoAggregateFunction.Count]: 'Anzahl',
    [SynqoAggregateFunction.Sum]: 'Summe',
    [SynqoAggregateFunction.Min]: 'Minimum',
    [SynqoAggregateFunction.Max]: 'Maximum',
    [SynqoAggregateFunction.Avg]: 'Durchschnitt',
};

const ORDER_PROCESS_STATE_VALUES = ['Open', 'InProgress', 'ExecutionFinished', 'Approved', 'Completed'] as const;

const PRICE_ORIGIN_VALUES = ['None', 'Manual', 'PriceList', 'Fixed'] as const;

const SERVICE_TYPE_VALUES = ['NotDefined', 'Material', 'Service', 'Textservice'] as const;

const SERVICE_UNIT_VALUES = [
    'Numerable',
    'FixedFee',
    'Percent',
    'Kilogram',
    'Ton',
    'Gram',
    'Liter',
    'CubicMeter',
    'Hours',
    'Days',
    'Kilometers',
] as const;

const COUNTRY_VALUES = [
    'Germany',
    'Austria',
    'Switzerland',
    'France',
    'Italy',
    'Netherlands',
    'Belgium',
    'Luxembourg',
    'Poland',
    'CzechRepublic',
    'Denmark',
] as const;

const FIELDS: readonly RulesetFieldMetadata[] = [
    // order shape
    { path: 'total_net_price', label: 'Nettobetrag (gesamt)', type: 'decimal' },
    { path: 'process_state', label: 'Auftragsstatus', type: 'enum', enumValues: ORDER_PROCESS_STATE_VALUES },
    { path: 'number', label: 'Nummer', type: 'string' },
    { path: 'customer', label: 'Kunde', type: 'string' },
    { path: 'customer.number', label: 'Kundennummer', type: 'string' },
    { path: 'customer.has_email_contact', label: 'Kunde hat E-Mail-Kontakt', type: 'bool' },
    { path: 'customer.tax_number', label: 'Steuernummer (Kunde)', type: 'string' },
    { path: 'customer.country', label: 'Land (Kunde)', type: 'enum', enumValues: COUNTRY_VALUES },
    { path: 'completed_at', label: 'Abschlussdatum', type: 'date' },
    // mandant ambient shape — addressed via {"kind":"ambient","shape":"mandant"}; the renderer looks
    // the path up in this same flat registry, so ambient fields live here alongside the entity ones.
    { path: 'today', label: 'Heute', type: 'date' },
    { path: 'positions', label: 'Positionen', type: 'string' },
    // order_position shape (item-relative — visitor descends via for_each(positions, …))
    { path: 'label', label: 'Positions-Bezeichnung', type: 'string' },
    { path: 'quantity', label: 'Menge', type: 'decimal' },
    { path: 'price_per_unit', label: 'Preis pro Einheit', type: 'decimal' },
    { path: 'price_origin', label: 'Preisherkunft', type: 'enum', enumValues: PRICE_ORIGIN_VALUES },
    { path: 'service.number', label: 'Leistungsnummer', type: 'string' },
    { path: 'service.type', label: 'Leistungsart', type: 'enum', enumValues: SERVICE_TYPE_VALUES },
    { path: 'service.unit', label: 'Leistungseinheit', type: 'enum', enumValues: SERVICE_UNIT_VALUES },
    { path: 'service.price_per_unit', label: 'Katalog-Preis pro Einheit', type: 'decimal' },
    // address shape
    { path: 'has_email_contact', label: 'Adresse hat E-Mail-Kontakt', type: 'bool' },
    { path: 'country', label: 'Land', type: 'enum', enumValues: COUNTRY_VALUES },
    { path: 'tax_number', label: 'Steuernummer', type: 'string' },
];

/** Global lookup shared by every ruleset detail view — covers all shapes in the app. */
export function lookupField(path: string): RulesetFieldMetadata | undefined {
    return FIELDS.find((f) => f.path === path);
}
